/* =====================================================================
 * AppManagePage — 앱 관리 (owner 전용)
 *
 * 실제 백엔드 API 연동: 사용자 목록 조회 · 검색 · 역할(admin ↔ user) 변경
 * 서버사이드 페이지네이션 (페이지당 10명)
 * ===================================================================== */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Globe, Loader2, MessageSquare, PanelLeft, RefreshCw, Zap } from 'lucide-react';
import { useChatStore, useUserStore } from '@auw/stores';
import { usePermission, ADMIN_USERS_VIEW } from '@auw/permissions';
import { listUsers, updateUserRole, adminUpdateUserPlan, getDailyUsageStats, type AppUserItem, type DailyUsageStatsResponse } from '@auw/api-client';
import type { PlanType, UserRole } from '@auw/workspace-utils';
import { PLANS } from '@auw/workspace-utils';
import { Input } from '@auw/workspace-ui';
import { Badge } from '@auw/workspace-ui';
import { useFeatureFlags } from '@auw/feature-flags';
import { exportUsersToXlsx } from './exportUsersXlsx';
import { UserActivityModal } from './UserActivityModal';
import { AdminErrorsPanel } from './AdminErrorsPanel';
import { AdminOverviewPanel } from './AdminOverviewPanel';
import { AdminUsagePanel } from './AdminUsagePanel';
import { AdminRecentActivityPanel } from './AdminRecentActivityPanel';

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 10;
/** 내보내기 시 전체 사용자를 한 번에 끌어오는 페이지 크기.
 *  백엔드 list_users 의 limit 상한(le=500)과 일치시킨다. */
const EXPORT_PAGE_SIZE = 500;
const ALL_PLANS: PlanType[] = ['free', 'pro', 'premium', 'max', 'business', 'enterprise'];

/** KST(Asia/Seoul) 기준 오늘 날짜 YYYY-MM-DD. 백엔드 일일 집계의 KST 일 경계와 정합시킨다.
 * (UTC 기준 날짜를 쓰면 KST 오전에 전날로 표시되는 문제가 있어 timeZone 을 명시한다.) */
const kstTodayISO = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());

/* 대시보드 탭. recent(최근 기록)는 admin.app.recentActivity 플래그로 노출. */
type AppManageTab = 'overview' | 'usage' | 'users' | 'recent' | 'errors';
const APP_MANAGE_TABS: AppManageTab[] = ['overview', 'usage', 'users', 'recent', 'errors'];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const AppManagePageInner: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const addToast = useChatStore((s) => s.addToast);
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  /* P11-5j: granular admin/app gates. Role/plan dropdowns and the daily
   * usage panel each have their own flag — disabling them collapses
   * those columns / sections without removing the broader user table. */
  const appFlags = useFeatureFlags();
  const roleChangeEnabled = appFlags.isEnabled('admin.app.roleChange');
  const planChangeEnabled = appFlags.isEnabled('admin.app.planChange');
  const usageDashboardEnabled = appFlags.isEnabled('admin.app.usageDashboard');
  const recentActivityEnabled = appFlags.isEnabled('admin.app.recentActivity');

  const [users, setUsers] = useState<AppUserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [planDropdown, setPlanDropdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState<string | null>(null);
  const [planUpdating, setPlanUpdating] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // 기본 탭: 종합 분석 '개요'.
  const [activeTab, setActiveTab] = useState<AppManageTab>('overview');
  // #4 활동 내역 모달 대상 사용자.
  const [activityUser, setActivityUser] = useState<{ id: string; name: string } | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* ---- 토큰 사용량 ---- */
  // 기본값은 KST 기준 '오늘'(백엔드 일일 집계가 KST 일 경계를 사용).
  const [usageDate, setUsageDate] = useState(kstTodayISO);
  const [usageStats, setUsageStats] = useState<DailyUsageStatsResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  /* ---- 드롭다운 외부 클릭 닫기 ---- */
  useEffect(() => {
    if (!openDropdown && !planDropdown) return;
    const handler = () => { setOpenDropdown(null); setPlanDropdown(null); };
    const timer = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handler); };
  }, [openDropdown, planDropdown]);

  /* ---- 데이터 로드 ---- */
  const fetchUsers = useCallback(
    async (searchQuery?: string, role?: string, pageNum: number = 1) => {
      setLoading(true);
      try {
        const res = await listUsers({
          q: searchQuery || undefined,
          role: role && role !== 'all' ? role : undefined,
          skip: (pageNum - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
        });
        setUsers(res.users);
        setTotal(res.total);
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.usersLoadFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  /* 초기 로드 */
  useEffect(() => {
    fetchUsers('', 'all', 1);
  }, [fetchUsers]);

  /* ---- 토큰 사용량 로드 ---- */
  const fetchUsageStats = useCallback(
    async (date: string) => {
      setUsageLoading(true);
      try {
        const stats = await getDailyUsageStats(date);
        setUsageStats(stats);
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.usageStatsFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setUsageLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    if (!usageDashboardEnabled) return;
    fetchUsageStats(usageDate);
  }, [fetchUsageStats, usageDate, usageDashboardEnabled]);

  /* ---- 검색 debounce (300ms) ---- */
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setPage(1);
        fetchUsers(value, roleFilter, 1);
      }, 300);
    },
    [fetchUsers, roleFilter],
  );

  /* ---- 역할 필터 변경 ---- */
  const handleRoleFilterChange = useCallback(
    (role: 'all' | UserRole) => {
      setRoleFilter(role);
      setPage(1);
      fetchUsers(query, role, 1);
    },
    [fetchUsers, query],
  );

  /* ---- 페이지 변경 ---- */
  const goToPage = useCallback(
    (p: number) => {
      const clamped = Math.max(1, Math.min(p, totalPages));
      setPage(clamped);
      fetchUsers(query, roleFilter, clamped);
    },
    [fetchUsers, query, roleFilter, totalPages],
  );

  /* ---- 엑셀 내보내기 ----
   * 현재 검색어 · 역할 필터 기준의 "전체" 사용자를 모두 끌어와(한 페이지 limit
   * 상한이 500이므로 필요 시 페이지를 이어서 조회) 한 행씩 .xlsx 로 만든다.
   * 화면은 페이지당 10명만 보여주지만, 내보내기는 전체를 대상으로 한다. */
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all: AppUserItem[] = [];
      let skip = 0;
      // 무한루프 방지를 위해 total 기준으로 종료한다.
      for (;;) {
        const res = await listUsers({
          q: query || undefined,
          role: roleFilter && roleFilter !== 'all' ? roleFilter : undefined,
          skip,
          limit: EXPORT_PAGE_SIZE,
        });
        all.push(...res.users);
        if (res.users.length === 0 || all.length >= res.total) break;
        skip += EXPORT_PAGE_SIZE;
      }

      if (all.length === 0) {
        addToast({ type: 'info', title: t('admin:appManage.export.empty') });
        return;
      }

      const rows = all.map((u) => ({
        user: u.name || '—',
        email: u.email || '—',
        role: roleDisplayName(u.role),
        plan: planDisplayName(u.plan || 'free'),
        joinDate: formatDate(u.created_at),
      }));

      exportUsersToXlsx(
        {
          user: t('admin:appManage.userColumn'),
          email: t('admin:appManage.emailColumn'),
          role: t('admin:appManage.roleColumn'),
          plan: t('admin:appManage.planColumn'),
          joinDate: t('admin:appManage.joinDateColumn'),
        },
        rows,
        `askyour-users-${kstTodayISO()}.xlsx`,
      );

      addToast({
        type: 'success',
        title: t('admin:appManage.export.success', { count: all.length }),
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: t('admin:appManage.export.fail'),
        description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
      });
    } finally {
      setExporting(false);
    }
  }, [exporting, query, roleFilter, addToast, t]);

  /* ---- 역할 변경 ---- */
  const handleRoleChange = useCallback(
    async (userId: string, newRole: UserRole) => {
      setOpenDropdown(null);
      setRoleUpdating(userId);
      try {
        await updateUserRole(userId, newRole);
        // 현재 페이지 갱신
        await fetchUsers(query, roleFilter, page);
        const target = users.find((u) => u.user_id === userId);
        addToast({
          type: 'success',
          title: t('admin:appManage.roleChangeSuccess'),
          description: `${target?.name ?? target?.email ?? ''} → ${roleDisplayName(newRole as UserRole)}`,
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.roleChangeFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setRoleUpdating(null);
      }
    },
    [addToast, fetchUsers, query, roleFilter, page, users],
  );

  /* ---- 플랜 변경 ---- */
  const handlePlanChange = useCallback(
    async (userId: string, newPlan: PlanType) => {
      setPlanDropdown(null);
      setPlanUpdating(userId);
      try {
        await adminUpdateUserPlan(userId, newPlan);
        await fetchUsers(query, roleFilter, page);
        const target = users.find((u) => u.user_id === userId);
        addToast({
          type: 'success',
          title: t('admin:appManage.planChangeSuccess'),
          description: `${target?.name ?? target?.email ?? ''} → ${planDisplayName(newPlan)}`,
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.planChangeFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setPlanUpdating(null);
      }
    },
    [addToast, fetchUsers, query, roleFilter, page, users],
  );

  /* ---- 페이지 번호 배열 생성 (최대 7개 표시) ---- */
  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <Wrap>
      {!sidebarOpen && (
        <SidebarToggleSlot>
          <SidebarToggleBtn onClick={() => setSidebarOpen(true)} title={t('common:storage.openSidebar')}>
            <PanelLeft size={18} />
          </SidebarToggleBtn>
        </SidebarToggleSlot>
      )}
      <PageHead>
        <PageTitle>{t('admin:appManage.pageTitle')}</PageTitle>
        <TabBar role="tablist">
          {APP_MANAGE_TABS.filter((tab) => tab !== 'recent' || recentActivityEnabled).map((tab) => (
            <TabBtn
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              $active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
            >
              {t(`admin:appManage.tabs.${tab}`)}
            </TabBtn>
          ))}
        </TabBar>
      </PageHead>

      {activeTab === 'users' && (
      <Card>
        <CardTitle>{t('admin:appManage.title')}</CardTitle>
        <CardDesc>{t('admin:appManage.description')}</CardDesc>

        {/* Search + Filter */}
        <ToolBar>
          <SearchWrap>
            <Input
              variant="search"
              placeholder={t('admin:appManage.searchPlaceholder')}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
            />
          </SearchWrap>
          <FilterPill>
            {(['all', 'owner', 'admin', 'user'] as const).map((r) => (
              <FilterBtn key={r} $active={roleFilter === r} onClick={() => handleRoleFilterChange(r)}>
                {r === 'all' ? t('admin:appManage.all') : roleDisplayName(r)}
              </FilterBtn>
            ))}
          </FilterPill>
          <ExportBtn
            type="button"
            onClick={handleExport}
            disabled={exporting || loading}
            title={t('admin:appManage.export.tooltip')}
          >
            {exporting ? <Loader2 size={14} className="spin" /> : <Download size={14} />}
            {t('admin:appManage.export.button')}
          </ExportBtn>
        </ToolBar>

        {/* Table */}
        <TableWrap>
          <Table>
            <thead>
              <Tr>
                <Th>{t('admin:appManage.userColumn')}</Th>
                <Th>{t('admin:appManage.emailColumn')}</Th>
                <Th>{t('admin:appManage.roleColumn')}</Th>
                {planChangeEnabled && <Th>{t('admin:appManage.planColumn')}</Th>}
                <Th>{t('admin:appManage.joinDateColumn')}</Th>
                {roleChangeEnabled && <Th style={{ width: 100 }}>{t('admin:appManage.manageColumn')}</Th>}
                <Th style={{ width: 96 }}>{t('admin:appManage.activity.column')}</Th>
              </Tr>
            </thead>
            <tbody>
              {(() => {
                const colCount = 5 + (planChangeEnabled ? 1 : 0) + (roleChangeEnabled ? 1 : 0);
                if (loading && users.length === 0) {
                  return (
                    <Tr>
                      <Td colSpan={colCount}>
                        <EmptyRow><Loader2 size={16} className="spin" /> {t('admin:appManage.loading')}</EmptyRow>
                      </Td>
                    </Tr>
                  );
                }
                if (users.length === 0) {
                  return (
                    <Tr>
                      <Td colSpan={colCount}>
                        <EmptyRow>{t('admin:appManage.noResults')}</EmptyRow>
                      </Td>
                    </Tr>
                  );
                }
                return users.map((u) => (
                  <Tr key={u.user_id}>
                    <Td>
                      <UserCell>
                        <UserAvatar>{avatarLetter(u.name, u.email)}</UserAvatar>
                        <UserName>{u.name || '—'}</UserName>
                      </UserCell>
                    </Td>
                    <Td><EmailText>{u.email || '—'}</EmailText></Td>
                    <Td>
                      <Badge variant={u.role === 'admin' || u.role === 'owner' ? 'info' : 'default'}>
                        {u.role === 'admin' || u.role === 'owner' ? t('admin:appManage.adminBadge') : t('admin:appManage.memberBadge')}
                      </Badge>
                    </Td>
                    {planChangeEnabled && (
                      <Td>
                        {planUpdating === u.user_id ? (
                          <DisabledText><Loader2 size={14} className="spin" /></DisabledText>
                        ) : (
                          <PlanDropdown>
                            <PlanBadgeBtn
                              $plan={u.plan || 'free'}
                              onClick={() => setPlanDropdown(planDropdown === u.user_id ? null : u.user_id)}
                            >
                              {planDisplayName(u.plan || 'free')} <ChevronDown size={10} />
                            </PlanBadgeBtn>
                            {planDropdown === u.user_id && (
                              <RoleDropdownMenu>
                                {ALL_PLANS.filter((p) => p !== (u.plan || 'free')).map((p) => (
                                  <RoleDropdownItem key={p} onClick={() => handlePlanChange(u.user_id, p)}>
                                    {planDisplayName(p)}
                                  </RoleDropdownItem>
                                ))}
                              </RoleDropdownMenu>
                            )}
                          </PlanDropdown>
                        )}
                      </Td>
                    )}
                    <Td><DateText>{formatDate(u.created_at)}</DateText></Td>
                    {roleChangeEnabled && (
                      <Td>
                        {u.role === 'owner' ? (
                          <DisabledText>—</DisabledText>
                        ) : roleUpdating === u.user_id ? (
                          <DisabledText><Loader2 size={14} className="spin" /></DisabledText>
                        ) : (
                          <RoleDropdown>
                            <RoleDropdownBtn
                              onClick={() => setOpenDropdown(openDropdown === u.user_id ? null : u.user_id)}
                            >
                              {t('admin:appManage.changeBtn')} <ChevronDown size={12} />
                            </RoleDropdownBtn>
                            {openDropdown === u.user_id && (
                              <RoleDropdownMenu>
                                {u.role !== 'admin' && (
                                  <RoleDropdownItem onClick={() => handleRoleChange(u.user_id, 'admin')}>
                                    {t('admin:appManage.promoteAdmin')}
                                  </RoleDropdownItem>
                                )}
                                {u.role !== 'user' && (
                                  <RoleDropdownItem onClick={() => handleRoleChange(u.user_id, 'user')}>
                                    {t('admin:appManage.demoteUser')}
                                  </RoleDropdownItem>
                                )}
                              </RoleDropdownMenu>
                            )}
                          </RoleDropdown>
                        )}
                      </Td>
                    )}
                    <Td>
                      <ActivityBtn
                        type="button"
                        onClick={() => setActivityUser({ id: u.user_id, name: u.name || u.email || u.user_id })}
                      >
                        {t('admin:appManage.activity.view')}
                      </ActivityBtn>
                    </Td>
                  </Tr>
                ));
              })()}
            </tbody>
          </Table>
        </TableWrap>

        {/* Footer: count + pagination */}
        <TableFooter>
          <CountLabel>
            {total === 0
              ? t('admin:appManage.countEmpty')
              : t('admin:appManage.countLabel', { start: (page - 1) * PAGE_SIZE + 1, end: Math.min(page * PAGE_SIZE, total), total })}
          </CountLabel>

          {totalPages > 1 && (
            <Pagination>
              <PageBtn disabled={page === 1} onClick={() => goToPage(1)} aria-label={t('admin:appManage.firstPage')}>
                <ChevronsLeft size={14} />
              </PageBtn>
              <PageBtn disabled={page === 1} onClick={() => goToPage(page - 1)} aria-label={t('admin:appManage.prevPage')}>
                <ChevronLeft size={14} />
              </PageBtn>

              {pageNumbers.map((p, i) =>
                p === '...' ? (
                  <PageEllipsis key={`e${i}`}>…</PageEllipsis>
                ) : (
                  <PageBtn
                    key={p}
                    $active={p === page}
                    onClick={() => goToPage(p as number)}
                  >
                    {p}
                  </PageBtn>
                ),
              )}

              <PageBtn disabled={page === totalPages} onClick={() => goToPage(page + 1)} aria-label={t('admin:appManage.nextPage')}>
                <ChevronRight size={14} />
              </PageBtn>
              <PageBtn disabled={page === totalPages} onClick={() => goToPage(totalPages)} aria-label={t('admin:appManage.lastPage')}>
                <ChevronsRight size={14} />
              </PageBtn>
            </Pagination>
          )}
        </TableFooter>
      </Card>
      )}

      {/* ---- 사용량 탭 ---- */}
      {activeTab === 'usage' && usageDashboardEnabled && (
      <Card>
        <UsageTitleRow>
          <UsageTitleLeft>
            <CardTitle>{t('admin:appManage.tokenUsage')}</CardTitle>
            <RefreshBtn
              onClick={() => fetchUsageStats(usageDate)}
              disabled={usageLoading}
              aria-label={t('admin:appManage.refresh')}
            >
              <RefreshCw size={14} className={usageLoading ? 'spin' : ''} />
            </RefreshBtn>
          </UsageTitleLeft>
          <DatePickerWrap>
            <Calendar size={14} />
            <DateInput
              type="date"
              value={usageDate}
              max={kstTodayISO()}
              onChange={(e) => setUsageDate(e.target.value)}
            />
          </DatePickerWrap>
        </UsageTitleRow>
        <CardDesc>{t('admin:appManage.usageUpdateNote')}</CardDesc>

        {usageLoading ? (
          <UsageLoadingWrap><Loader2 size={18} className="spin" /> {t('admin:appManage.usageLoading')}</UsageLoadingWrap>
        ) : usageStats ? (
          <StatsGrid>
            <StatCard>
              <StatIconWrap $color="#3b82f6"><Zap size={18} /></StatIconWrap>
              <StatContent>
                <StatLabel>{t('admin:appManage.dailyTokenUsage')}</StatLabel>
                <StatValue>{formatTokens(usageStats.total_tokens)}</StatValue>
                <StatSub>{t('admin:appManage.totalUserSum')}</StatSub>
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconWrap $color="#10b981"><Globe size={18} /></StatIconWrap>
              <StatContent>
                <StatLabel>{t('admin:appManage.countryTokenUsage')}</StatLabel>
                {usageStats.by_country.length === 0 ? (
                  <StatSub>{t('admin:appManage.noData')}</StatSub>
                ) : (
                  <CountryList>
                    {usageStats.by_country.slice(0, 5).map((c) => (
                      <CountryRow key={c.country}>
                        <CountryName>{t(`admin:appManage.countries.${c.country}`, c.country)}</CountryName>
                        <CountryTokens>{formatTokens(c.tokens)}</CountryTokens>
                        <CountrySub>{t('admin:appManage.membersUnit', { count: c.user_count })}</CountrySub>
                      </CountryRow>
                    ))}
                    {usageStats.by_country.length > 5 && (
                      <StatSub>{t('admin:appManage.moreCountries', { count: usageStats.by_country.length - 5 })}</StatSub>
                    )}
                  </CountryList>
                )}
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconWrap $color="#8b5cf6"><MessageSquare size={18} /></StatIconWrap>
              <StatContent>
                <StatLabel>{t('admin:appManage.featureEvents')}</StatLabel>
                {usageStats.by_feature && usageStats.by_feature.length > 0 ? (
                  <CountryList>
                    {usageStats.by_feature.map((f) => (
                      <CountryRow key={f.feature}>
                        <CountryName>{t(`admin:appManage.features.${f.feature}`, f.feature)}</CountryName>
                        <CountryTokens>{f.count.toLocaleString()}</CountryTokens>
                        <CountrySub>{t('admin:appManage.eventUnit')}</CountrySub>
                      </CountryRow>
                    ))}
                  </CountryList>
                ) : (
                  <>
                    <StatValue>{usageStats.chat_events.toLocaleString()}</StatValue>
                    <StatSub>{t('admin:appManage.eventUnit')}</StatSub>
                  </>
                )}
              </StatContent>
            </StatCard>
          </StatsGrid>
        ) : (
          <UsageLoadingWrap>{t('admin:appManage.selectDate')}</UsageLoadingWrap>
        )}
      </Card>
      )}

      {activeTab === 'usage' && !usageDashboardEnabled && (
        <Card><ScaffoldNote>{t('admin:appManage.usageDisabled')}</ScaffoldNote></Card>
      )}

      {activeTab === 'usage' && (
        <Card style={{ marginTop: 16 }}>
          <CardTitle>{t('admin:appManage.usageTab.title')}</CardTitle>
          <CardDesc>{t('admin:appManage.usageTab.description')}</CardDesc>
          <AdminUsagePanel />
        </Card>
      )}

      {activeTab === 'overview' && (
        <Card>
          <CardTitle>{t('admin:appManage.overview.title')}</CardTitle>
          <CardDesc>{t('admin:appManage.overview.description')}</CardDesc>
          <AdminOverviewPanel />
        </Card>
      )}

      {activeTab === 'recent' && recentActivityEnabled && (
        <Card>
          <CardTitle>{t('admin:appManage.recent.title')}</CardTitle>
          <CardDesc>{t('admin:appManage.recent.description')}</CardDesc>
          <AdminRecentActivityPanel />
        </Card>
      )}

      {activeTab === 'errors' && (
        <Card>
          <CardTitle>{t('admin:appManage.errors.title')}</CardTitle>
          <CardDesc>{t('admin:appManage.errors.description')}</CardDesc>
          <AdminErrorsPanel />
        </Card>
      )}

      {activityUser && (
        <UserActivityModal
          userId={activityUser.id}
          userName={activityUser.name}
          onClose={() => setActivityUser(null)}
        />
      )}
    </Wrap>
  );
};

/* ------------------------------------------------------------------ */
/* Owner guard (보안) — 비-owner는 admin shell 자체를 렌더하지 않는다.       */
/* 서버 엔드포인트(_require_owner)도 403을 강제하므로 이중 방어.            */
/* ------------------------------------------------------------------ */
export const AppManagePage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const addToast = useChatStore((s) => s.addToast);
  const navigate = useNavigate();
  // 사용자 로드 전에는 role 이 기본값(user)으로 떨어져 권한이 false 가 될 수 있으므로
  // isLoaded 가 true 가 된 뒤에만 권한을 판정한다(실제 owner 가 새로고침 시 튕기는 것 방지).
  const isUserLoaded = useUserStore((s) => s.isLoaded);
  const canViewAdmin = usePermission(ADMIN_USERS_VIEW);
  const deniedNotified = useRef(false);

  useEffect(() => {
    if (isUserLoaded && !canViewAdmin && !deniedNotified.current) {
      deniedNotified.current = true;
      addToast({ type: 'error', title: t('admin:appManage.ownerOnly') });
      navigate('/', { replace: true });
    }
  }, [isUserLoaded, canViewAdmin, addToast, navigate, t]);

  // 로드 전 / 권한 없음: admin UI 셸을 노출하지 않는다(리다이렉트 진행 중).
  if (!isUserLoaded || !canViewAdmin) return null;
  return <AppManagePageInner />;
};

export default AppManagePage;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function roleDisplayName(role: UserRole | string): string {
  switch (role) {
    case 'owner': return 'Owner';
    case 'admin': return 'Admin';
    case 'user': return 'User';
    default: return role;
  }
}

function planDisplayName(plan: string): string {
  const info = PLANS[plan as PlanType];
  return info ? info.name : plan;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}


function avatarLetter(name: string | null, email: string | null): string {
  const src = (name || email || '?').trim();
  const ch = src.charAt(0);
  return /[\u3131-\uD79D]/.test(ch) ? ch : ch.toUpperCase();
}

function formatDate(raw: string | null): string {
  if (!raw) return '—';
  try {
    return raw.slice(0, 10);
  } catch {
    return raw;
  }
}

/** 표시할 페이지 번호 배열 생성 (최대 7개, 줄임표 포함) */
function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [];

  if (current <= 4) {
    // 앞쪽: 1 2 3 4 5 ... last
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push('...');
    pages.push(total);
  } else if (current >= total - 3) {
    // 뒷쪽: 1 ... n-4 n-3 n-2 n-1 n
    pages.push(1);
    pages.push('...');
    for (let i = total - 4; i <= total; i++) pages.push(i);
  } else {
    // 중간: 1 ... cur-1 cur cur+1 ... last
    pages.push(1);
    pages.push('...');
    pages.push(current - 1);
    pages.push(current);
    pages.push(current + 1);
    pages.push('...');
    pages.push(total);
  }

  return pages;
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 80px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px 12px 64px;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SidebarToggleSlot = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 5;
`;

const SidebarToggleBtn = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ---- Tabs (대시보드 셸) ---- */

const PageHead = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto 16px;
`;

const PageTitle = styled.h1`
  margin: 0 0 14px;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  /* 좁은 화면에서 탭 라벨이 길어져도 줄바꿈 대신 가로 스크롤로 한 줄 유지 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const TabBtn = styled.button<{ $active?: boolean }>`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 14px;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.textPrimary : 'transparent')};
  margin-bottom: -1px;
  white-space: nowrap;
  transition: color 0.15s ease;
  &:hover { color: ${({ theme }) => theme.colors.textPrimary}; }
`;

const ScaffoldNote = styled.div`
  padding: 48px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ActivityBtn = styled.button`
  appearance: none;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 4px 10px;
  font-size: 0.75rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
`;

const Card = styled.main`
  width: 100%;
  max-width: 900px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  padding: 36px 36px 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 16px 20px;
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
  }
`;

const CardTitle = styled.h1`
  margin: 0 0 4px;
  font-size: 1.375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const CardDesc = styled.p`
  margin: 0 0 24px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Toolbar ---- */

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  /* 모바일: 검색 · 필터 · 내보내기를 세로로 쌓아 각 행을 가득 채운다. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 0;
  }
`;


const FilterPill = styled.div`
  display: inline-flex;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  flex-shrink: 0;

  /* 모바일: 폭을 가득 채우고 4개 세그먼트를 균등 분할 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
    width: 100%;
  }
`;

const FilterBtn = styled.button<{ $active: boolean }>`
  padding: 7px 14px;
  border: none;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: 1;
    text-align: center;
  }

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSecondary};
          &:hover { background: ${theme.colors.bgHover}; }
        `}
`;

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 7px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    border-color: ${({ theme }) => theme.colors.borderInput};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.5; cursor: default; }

  /* 모바일: 폭을 가득 채우고 가운데 정렬 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
    padding: 9px 14px;
  }
`;

/* ---- Table ---- */

const TableWrap = styled.div`
  /* 데스크톱: 역할·플랜 드롭다운 메뉴가 셀 밖으로 펼쳐져야 하므로 visible 유지. */
  overflow: visible;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};

  /* 모바일: 표가 카드를 넘쳐 페이지가 깨지지 않도록 가로 스크롤로 가둔다. */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'};
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  vertical-align: middle;
`;

const EmptyRow = styled.div`
  text-align: center;
  padding: 32px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

/* ---- User cell ---- */

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#3a3a3a' : '#e8e5e1')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d4d0cc' : '#5c5550')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  flex-shrink: 0;
`;

const UserName = styled.span`
  font-weight: 500;
  white-space: nowrap;
`;

const EmailText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const DisabledText = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  display: inline-flex;
  align-items: center;
`;

/* ---- Plan badge dropdown ---- */

const PlanDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const PLAN_COLORS: Record<string, { bg: string; color: string }> = {
  free: { bg: 'rgba(128,128,128,0.12)', color: '#888' },
  pro: { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
  premium: { bg: 'rgba(139,92,246,0.12)', color: '#8b5cf6' },
  max: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  business: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
  enterprise: { bg: 'rgba(236,72,153,0.12)', color: '#ec4899' },
};

const PlanBadgeBtn = styled.button<{ $plan: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: none;
  border-radius: 12px;
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ $plan }) => PLAN_COLORS[$plan]?.bg ?? PLAN_COLORS.free.bg};
  color: ${({ $plan }) => PLAN_COLORS[$plan]?.color ?? PLAN_COLORS.free.color};
  &:hover { opacity: 0.8; }
`;

/* ---- Role dropdown ---- */

const RoleDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const RoleDropdownBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: none;
  border-radius: 12px;
  background: rgba(128,128,128,0.12);
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover { opacity: 0.8; }
`;

const RoleDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 20;
  min-width: 150px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 4px;
  animation: fadeIn 0.12s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const RoleDropdownItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
`;

/* ---- Footer + Pagination ---- */

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 12px;

  /* 모바일: 카운트와 페이지네이션을 세로로 쌓아 가운데 정렬 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CountLabel = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Pagination = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;

  /* 모바일: 페이지 버튼이 많아도 가로 넘침 없이 줄바꿈 */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const PageBtn = styled.button<{ $active?: boolean; disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.75rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
          border-color: ${theme.colors.textPrimary};
          pointer-events: none;
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSecondary};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgHover};
            border-color: ${theme.colors.border};
          }
        `}

  &:disabled {
    opacity: 0.35;
    cursor: default;
    pointer-events: none;
  }
`;

const PageEllipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  user-select: none;
`;

/* ---- Token Usage Section ---- */

const UsageTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

const UsageTitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const DatePickerWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.fast};
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
`;

const DateInput = styled.input`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  font-family: inherit;
  color-scheme: ${({ theme }) => (theme.mode === 'dark' ? 'dark' : 'light')};

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const UsageLoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  display: flex;
  gap: 14px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};
`;

const StatIconWrap = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $color }) => $color}18;
  color: ${({ $color }) => $color};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
`;

const StatSub = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CountryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const CountryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
`;

const CountryName = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 48px;
`;

const CountryTokens = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const CountrySub = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;
